import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Spacing, Colors, Palette } from '@/shared/theme';
import { Button, Title, Text, CenteredColumn } from '@/shared/ui';
import SelectionCard from '@/shared/SelectionCard';
import { LearnMoreModal } from '@/components/LearnMoreModal';
import { StartOverModal } from '@/components/StartOverModal';
import { useRegistration } from '@/context/RegistrationContext';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useEncryptedEventsExternal } from '@/hooks/useEncryptedEventsExternal';
import { useAppFlow, STEP_KEYS } from '@/hooks/useAppFlow';
import type { TrackType } from '@/types/registration';
import { VIEW_PROGRAM, CONTINUE_WEBREG } from '@/consts/events';
import { ENCRYPTED_BUTTON_PRESSED_TRACK_OFFERING_CONTINUE } from '@/consts/encryptedEvents';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  gap: ${Spacing.x3}px;
`;

const HeadingWrap = styled.div`
  width: 100%;
  text-align: left;
`;

const SubtitleText = styled(Text)`
  margin-top: ${Spacing.x1_5}px;
  margin-bottom: ${Spacing.x3}px;
  width: 100%;
  text-align: left;
`;

const LearnMoreLink = styled.button`
  background: none;
  border: none;
  color: ${Palette.helloTeal};
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  line-height: 24px;
  text-decoration: underline;
  cursor: pointer;
  padding: ${Spacing.x2}px 0;
  margin-bottom: ${Spacing.x2}px;

  &:hover {
    opacity: 0.8;
  }
`;

const TracksContainer = styled.div`
  width: 100%;
  max-width: 560px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: ${Spacing.x1_5}px;
`;

const ErrorText = styled(Text)`
  color: ${Colors.error};
  margin-top: ${Spacing.x1}px;
`;

const Footer = styled.div`
  margin-top: ${Spacing.x3}px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${Spacing.x1}px;
`;

const TRACK_CONFLICTS: Readonly<Partial<Record<TrackType, TrackType>>> = {
  eCKM: 'CKM',
  CKM: 'eCKM',
};

const TrackOfferingStep: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { registrationData, updateSelectedTracks } = useRegistration();
  const { trackView, track } = useAnalytics();
  const { trackEncryptedEvent } = useEncryptedEventsExternal();
  const [selectedTracks, setSelectedTracks] = useState<Set<TrackType>>(new Set());
  const [showError, setShowError] = useState(false);
  const [isLearnMoreOpen, setIsLearnMoreOpen] = useState(false);
  const [showStartOverModal, setShowStartOverModal] = useState(false);

  // Integrate useAppFlow with back navigation blocking
  const { navigateToNextStep, resetBlocker } = useAppFlow(STEP_KEYS.TRACK_OFFERING, {
    onBackNavigationBlocked: () => {
      setShowStartOverModal(true);
    },
  });

  const eligibility = registrationData.eligibility;
  const eligibleTracks = useMemo(() => eligibility?.eligibleTracks || [], [eligibility]);

  useEffect(() => {
    // Convert tracks to lowercase for analytics (e.g., 'eCKM' -> 'eckm')
    const programPresented =
      eligibleTracks.length === 1
        ? eligibleTracks[0].toLowerCase()
        : eligibleTracks.map((track) => track.toLowerCase());

    trackView({
      event: VIEW_PROGRAM,
      properties: {
        program_presented: programPresented,
      },
    });
  }, [eligibleTracks, trackView]);

  const handleTrackToggle = (value: string) => {
    setShowError(false);
    const trackType = value as TrackType;
    const newSelected = new Set(selectedTracks);
    if (newSelected.has(trackType)) {
      newSelected.delete(trackType);
    } else {
      newSelected.add(trackType);
      // eCKM and CKM are mutually exclusive -- deselect the other if present.
      // UI safety net: eligibility guarantees co-occurrence won't reach here,
      // but this guard handles it defensively at the component level.
      const conflict = TRACK_CONFLICTS[trackType];
      if (conflict !== undefined) newSelected.delete(conflict);
    }
    setSelectedTracks(newSelected);
  };

  // User must select at least one track to continue
  const hasSelection = selectedTracks.size > 0;

  const handleLearnMoreClick = () => {
    setIsLearnMoreOpen(true);
  };

  const handleLearnMoreClose = () => {
    setIsLearnMoreOpen(false);
  };

  const handleContinue = () => {
    // Prepare analytics properties
    const programPresented =
      eligibleTracks.length === 1
        ? eligibleTracks[0].toLowerCase()
        : eligibleTracks.map((track) => track.toLowerCase());

    const selectedTracksArray = Array.from(selectedTracks);
    const programChosen =
      selectedTracksArray.length === 1
        ? selectedTracksArray[0].toLowerCase()
        : selectedTracksArray.map((track) => track.toLowerCase());

    track({
      event: CONTINUE_WEBREG,
      properties: {
        source: 'program_access',
        program_presented: programPresented,
        program_chosen: programChosen,
      },
    });

    if (!hasSelection) {
      setShowError(true);
      return;
    }

    // Encrypted external events tracking
    trackEncryptedEvent({
      event: ENCRYPTED_BUTTON_PRESSED_TRACK_OFFERING_CONTINUE,
      properties: {
        selectedTracks: selectedTracksArray, // Send as array of strings
      },
    });

    // Save selected tracks to context before navigating
    updateSelectedTracks(Array.from(selectedTracks));
    navigateToNextStep();
  };

  const handleStartOver = () => {
    // Navigate to landing page (which will clear all data on mount)
    setShowStartOverModal(false);
    navigate('/');
  };

  // Track configuration for display (only titles now)
  const trackConfig: Record<TrackType, { titleKey: string }> = {
    eCKM: {
      titleKey: 'track-offering.eckm-title',
    },
    CKM: {
      titleKey: 'track-offering.ckm-title',
    },
    BH: {
      titleKey: 'track-offering.bh-title',
    },
  };

  return (
    <Container>
      <CenteredColumn span={8}>
        <HeadingWrap>
          <Title size="h2">
            {t('track-offering.title')}
          </Title>
        </HeadingWrap>

        <SubtitleText size="p1">{t('track-offering.subtitle')}</SubtitleText>

        <TracksContainer role="group" aria-label={t('track-offering.title')}>
          {eligibleTracks.map((track) => (
            <SelectionCard
              key={track}
              value={track}
              title={t(trackConfig[track].titleKey)}
              selected={selectedTracks.has(track)}
              onClick={handleTrackToggle}
              titleAlign="end"
              type="checkbox"
            />
          ))}
        </TracksContainer>

        <Footer>
          <div role="alert">
            {showError && !hasSelection && (
              <ErrorText size="p3" centered>
                {t('track-offering.selection-required')}
              </ErrorText>
            )}
          </div>
          <CenteredColumn span={6}>
            <Button
              text={t('general.continue')}
              onClick={handleContinue}
              onDisabledClick={handleContinue}
              variant={hasSelection ? 'brand-primary' : 'disabled'}
            />
          </CenteredColumn>
          <LearnMoreLink onClick={handleLearnMoreClick}>
            {t('modals.learn-more.link-text')}
          </LearnMoreLink>
        </Footer>
      </CenteredColumn>

      <LearnMoreModal
        isOpen={isLearnMoreOpen}
        onClose={handleLearnMoreClose}
        eligibleTracks={eligibleTracks}
      />

      {/* Start Over Modal */}
      <StartOverModal
        isOpen={showStartOverModal}
        onConfirm={handleStartOver}
        onCancel={() => {
          setShowStartOverModal(false);
          resetBlocker();
        }}
      />
    </Container>
  );
};

export { TrackOfferingStep };
